import { CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';

import { useNextInfiniteRequest } from '@/renderer/utils/api/infiniteStore';

import { ResultsTableContent } from './ResultsTable';
import { resultsListOpts } from './consts';

// This section is futures-only. Futures are deliberately kept OUT of the
// game-pending section (they sit ungraded for months and would bog it down), so
// they get their own home here (#1581).
const FUTURE_PLAY_TYPES = ['future'] as const;

// Show the member's whole current pending futures slate at once (it's small).
const FUTURES_LIMIT = 100;

/**
 * The signed-in member's PENDING futures for the selected sport(s), pinned in the
 * "Futures" tab of My Results. Until now a member's pending futures showed nowhere
 * (the graded table only lists graded results and the game-pending section excludes
 * futures), even ones received in real time (#1581).
 *
 * Counting mirrors the game-pending section exactly, reusing the same receipt +
 * ghost machinery:
 *   - Received in real time (receipt, not excluded) -> counts toward the record
 *     once graded.
 *   - Ghost (visible via an active subscription but no receipt, e.g. released
 *     before the member subscribed) -> SHOWN but never counts, styled as temporary.
 *
 * Graded futures continue to appear under the graded results and count in the
 * record exactly as before — this tab surfaces the currently-pending ones.
 *
 * Only renders on the member's own view (userId === 'me').
 */
export function MemberFuturesSection({
  userId,
  sportId,
  serviceIds,
  active = true,
}: {
  userId: API.Id | 'me' | 'archive';
  sportId?: number | number[];
  serviceIds?: number[];
  // Only fetch once the Futures tab is actually opened.
  active?: boolean;
}) {
  const request = useNextInfiniteRequest<
    API.MessageReceipt,
    void,
    API.WithPageInput<API.ExtractNestedSortable<API.MessageReceipt>>
  >(
    useMemo(
      () =>
        resultsListOpts({
          key: 'fetchPendingFutures',
          req: `/api/v1/messageReceipt/${userId}`,
          query: {
            // Nested dot-path: a receipt reaches kickoff through its release. A bare
            // 'gameTime' is not a key of the receipt feed, so it fell back to `id`
            // (#2079). Ascending: the future that settles first leads.
            order: { by: 'release.gameTime', dir: 'ASC' },
            page: { limit: FUTURES_LIMIT, offset: 0 },
            data: {
              type: 'release',
              // result=pending + type=release routes the request through the
              // server's ghost augmentation (getPendingWithGhosts); the
              // futures-only playType scopes it to the futures ghost pass.
              result: 'pending',
              playType: [...FUTURE_PLAY_TYPES],
              [Array.isArray(sportId) ? 'sportId$in' : 'sportId']: sportId,
              'message.serviceIds': serviceIds,
            },
          },
          skipPoll: true,
          pause: userId !== 'me' || !active,
        }),
      [userId, sportId, serviceIds, active],
    ),
  );

  if (userId !== 'me') {
    return null;
  }

  const rows = request.data?.data ?? [];
  const settled = !request.isLoading && !request.isPending;
  const isEmpty = settled && rows.length === 0;
  // "Ghost" futures: visible via the member's subscription without a receipt (e.g.
  // released before they subscribed). They don't count on the record and drop off
  // shortly after grading.
  const hasGhosts = rows.some((row) => (row as API.MessageReceipt).ghost);

  return (
    <section className="mb-8">
      <h2 className="mb-3 flex items-center gap-2 font-semibold text-gray-900 text-lg dark:text-gray-100">
        Futures
        {rows.length > 0 && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800 text-xs dark:bg-amber-400/20 dark:text-amber-300">
            {rows.length}
          </span>
        )}
      </h2>

      {isEmpty ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-zinc-300 px-6 py-10 text-center dark:border-zinc-700">
          <CheckCircleIcon className="h-8 w-8 text-emerald-500" />
          <p className="font-medium text-gray-900 dark:text-gray-100">
            No pending futures right now
          </p>
          <p className="max-w-sm text-sm text-gray-500 dark:text-gray-400">
            Season-long futures you&rsquo;re out on will appear here. Graded futures roll into your
            record and show under the graded results.
          </p>
        </div>
      ) : (
        <>
          <ResultsTableContent userId={userId} infiniteRequest={request} hidePagination />
          {hasGhosts && (
            <p className="mt-3 flex items-center gap-1.5 text-xs text-zinc-500 dark:text-zinc-400">
              <ClockIcon className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-300" />
              Futures released before your subscription will not be counted and will disappear
              shortly after they have been graded.
            </p>
          )}
        </>
      )}
    </section>
  );
}
